import React, { useRef, useState } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { Platform, StatusBar, Text, View } from 'react-native';
import { InnspireChatBot } from './Apps/chat/innspireChatBot';
import FadeTransition from './components/fadeTransition';
import { HomeNavigator } from './navigation/StackNavigator/stackNavigator';
import GuestRoomStatus from './Apps/guestRoomStatus/index.js';
import FetchingDataScreen from './components/fetchingDataScreen/index.js';
import FlashMessageComp from './components/flashMessage/flashMessage';
import { useCssStyle } from './customHook/getCssStyle.js';
import AutoLoginSuccessModal from './Apps/guestRoomStatus/AutoLoginSuccessModal.js';
import InfoPopupModal from './components/infoPopupModal/index.js';
import KeyboardAvoidingWrapper from './components/keyboardAvoidingWrapper/index.js';
import OfflineModal from './components/offlineModal/index.js';
import { AdminPanel } from './Apps/AdminPanel/index.js';
import { AppInfoModal } from './Apps/guestRoomStatus/appInfoModal.js';
import { CustomPreLoader } from './Apps/startLoader/preLoader';
import { ISINROOMTAB } from './config/Constant';
import { setRenderInfoPopup } from './Redux/Actions/appConfigAction';

// Custom hooks for separating concerns
import { useAppLifecycle } from './customHook/useAppLifecycle';
import { useLayoutManager } from './customHook/useLayoutManager';
import { useInitializations } from './customHook/useInitializations';
import { useBookingState } from './customHook/useBookingState';
import { useHotelSelection } from './customHook/useHotelSelection';
import { useDeepLink } from './customHook/useDeepLink';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import BookmarkPopup from './components/bookmarkPopup/index.js';



/**
 * RootApp Component
 * Main application orchestrator - refactored to use custom hooks for better maintainability
 *
 * Custom hooks handle:
 * - useBookingState: Booking and pre-checkin data management
 * - useHotelSelection: Hotel switching and selection logic
 * - useLayoutManager: Layout mapping and CSS loading
 * - useAppLifecycle: App state and network monitoring
 * - useDeepLink: Deep linking navigation
 * - useInitializations: Door keys, chat, FCM tokens
 * - useNavigationHelpers: Navigation, login flow, logout
 */
export const RootApp = React.memo(() => {
    const getStyles = useCssStyle();
    const innspireChatRef = useRef(null);
    const insets = useSafeAreaInsets();

    // Initialize all custom hooks
    useAppLifecycle(innspireChatRef);
    useLayoutManager();
    useInitializations();
    useBookingState();
    useHotelSelection();
    useDeepLink();


    // Select only the UI state needed for rendering
    const {
        renderApp,
        renderLoader,
        renderFlashMessage,
        renderInfoPopup,
        showLoginModal,
        overlayVisible,
        isOnline,
        showOfflineMessage,
        renderAdminModal,
        isAutoLoggedIn,
        isLoggedIn,
        chatInterface
    } = useSelector(state => ({
        renderApp: state.appConfigSlice.renderApp,
        renderLoader: state.appConfigSlice.renderLoader,
        renderFlashMessage: state.appConfigSlice.renderFlashMessage,
        renderInfoPopup: state.appConfigSlice.renderInfoPopup,
        showLoginModal: state.appConfigSlice.showLoginModal,
        overlayVisible: state.appConfigSlice.overlayVisible,
        isOnline: state.appConfigSlice.isOnline,
        showOfflineMessage: state.appConfigSlice.showOfflineMessage,
        renderAdminModal: state.appConfigSlice.renderAdminModal,
        isAutoLoggedIn: state.appConfigSlice.isAutoLoggedIn,
        isLoggedIn: state.appConfigSlice.isLoggedIn,
        chatInterface: state.appPreservedConfigSlice.chatInterface
    }), shallowEqual);

    const dispatch = useDispatch();

    // All business logic is handled by custom hooks above
    // This component only handles rendering

    return (
        <View style={getStyles(['rootContainer', Platform.OS === 'android' && {marginBottom: insets.bottom}])}>
            <StatusBar translucent backgroundColor={"transparent"} />

            {/* ==================== LAYER 1: Main Navigation/Content ==================== */}
            <KeyboardAvoidingWrapper keyboardVerticalOffset={0}>
                {renderApp ? (
                    <FadeTransition duration={300}>
                        <HomeNavigator />
                    </FadeTransition>
                ) : (
                    <CustomPreLoader />
                )}
            </KeyboardAvoidingWrapper>

            {/* Only render overlays/modals when app is ready */}
            {renderApp && (
                <>
                    {/* ==================== LAYER 2: Overlay/Backdrop ==================== */}
                    {/* Dims background and prevents interaction - must come BEFORE modals */}
                    {overlayVisible && (
                        <View style={getStyles(['customOverlayContainer'])} />
                    )}

                    {/* ==================== LAYER 3: Blocking Modals ==================== */}
                    {/* User must interact with these before continuing */}
                    {showLoginModal && (
                        ISINROOMTAB ? <AppInfoModal /> : <GuestRoomStatus />
                    )}

                    {renderInfoPopup.isVisible && (
                        <InfoPopupModal
                            type={renderInfoPopup.popupConfig.type}
                            title={renderInfoPopup.popupConfig.title}
                            subtitle={renderInfoPopup.popupConfig.subtitle}
                            onClose={() => renderInfoPopup.popupConfig?.onClose?.() || dispatch(setRenderInfoPopup(false))}
                            OnRequestClose={() => renderInfoPopup.popupConfig?.onClose?.() || dispatch(setRenderInfoPopup(false))}
                            secondaryBtnLabel={renderInfoPopup.popupConfig.secondaryBtnLabel}
                            onContinue={renderInfoPopup.popupConfig?.onContinue ? () => renderInfoPopup.popupConfig?.onContinue?.() : null}
                            primaryBtnLabel={renderInfoPopup.popupConfig.primaryBtnLabel}
                        />
                    )}

                    {(isAutoLoggedIn && isLoggedIn) && <AutoLoginSuccessModal />}

                    {renderAdminModal && <AdminPanel />}

                    {/* ==================== LAYER 4: Floating/Non-blocking UI ==================== */}
                    {/* Chat and notifications float above modals but don't block */}
                    {isOnline && chatInterface?.interfaceKey?.toLowerCase() === "innspirechat" && (
                        <InnspireChatBot ref={innspireChatRef} />
                    )}

                    {renderFlashMessage.isVisible && (
                        <FlashMessageComp messageConfig={renderFlashMessage.messageConfig} />
                    )}

                    {/* ==================== LAYER 5: System Overlays (Topmost) ==================== */}
                    {/* Critical system states - should be above everything */}
                    {renderLoader.isVisible && (
                        <FetchingDataScreen loaderMessage={renderLoader.loaderMessage} />
                    )}

                    

                    {showOfflineMessage && <OfflineModal />}
                </>
            )}
        </View>
    );
});